import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

/**
 * Generate a B2B-style quote PDF from the quote list.
 *
 * @param {object} options
 * @param {Array}  options.items        Quote items (from QuoteContext)
 * @param {object} options.contact      Optional contact details (company/name/email/phone/orgNumber)
 * @param {object} options.siteSettings Optional { logo_url, company_name }
 * @param {string} options.language     'sv' | 'en'
 * @returns {jsPDF} generated document (caller can .save() / .output())
 */
export const generateQuotePdf = async ({
  items = [],
  contact = {},
  siteSettings = null,
  language = 'sv',
}) => {
  const isSv = language === 'sv';
  const t = (sv, en) => (isSv ? sv : en);

  const doc = new jsPDF({ unit: 'mm', format: 'a4' });
  const pageWidth = doc.internal.pageSize.getWidth();
  const margin = 15;

  // ---------- HEADER ----------
  // Try to render a logo if provided
  let cursorY = 18;
  const companyName = siteSettings?.company_name || 'FLOWINGO AB';

  if (siteSettings?.logo_url) {
    try {
      const logoUrl = siteSettings.logo_url.startsWith('/')
        ? `${process.env.REACT_APP_BACKEND_URL}${siteSettings.logo_url}`
        : siteSettings.logo_url;
      const dataUrl = await fetchAsDataUrl(logoUrl);
      if (dataUrl) {
        const imgProps = doc.getImageProperties(dataUrl);
        const targetH = 12;
        const targetW = (imgProps.width / imgProps.height) * targetH;
        doc.addImage(dataUrl, imgProps.fileType || 'PNG', margin, 12, Math.min(targetW, 60), targetH);
      } else {
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(20);
        doc.setTextColor('#321a27');
        doc.text(companyName, margin, cursorY);
      }
    } catch (err) {
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(20);
      doc.setTextColor('#321a27');
      doc.text(companyName, margin, cursorY);
    }
  } else {
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(20);
    doc.setTextColor('#321a27');
    doc.text(companyName, margin, cursorY);
  }

  // Right-aligned heading "OFFERTFÖRFRÅGAN"
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(16);
  doc.setTextColor('#1c1917');
  const headerLabel = t('OFFERTFÖRFRÅGAN', 'QUOTE REQUEST');
  doc.text(headerLabel, pageWidth - margin, 18, { align: 'right' });

  // Date and reference
  const now = new Date();
  const dateStr = now.toLocaleDateString(isSv ? 'sv-SE' : 'en-GB', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
  const refNumber = `Q-${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}${String(now.getDate()).padStart(2, '0')}-${Math.random().toString(36).slice(2, 6).toUpperCase()}`;

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor('#57534e');
  doc.text(`${t('Datum', 'Date')}: ${dateStr}`, pageWidth - margin, 24, { align: 'right' });
  doc.text(`${t('Referens', 'Reference')}: ${refNumber}`, pageWidth - margin, 28.5, { align: 'right' });

  // Burgundy divider
  cursorY = 34;
  doc.setDrawColor('#321a27');
  doc.setLineWidth(0.6);
  doc.line(margin, cursorY, pageWidth - margin, cursorY);
  cursorY += 8;

  // ---------- CONTACT SECTION ----------
  const hasContact = contact && (contact.company || contact.name || contact.email);
  if (hasContact) {
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.setTextColor('#1c1917');
    doc.text(t('Kunduppgifter', 'Customer details'), margin, cursorY);
    cursorY += 6;

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.setTextColor('#44403c');
    const lines = [];
    if (contact.company) lines.push(contact.company);
    if (contact.orgNumber) lines.push(`${t('Org.nr', 'Org. no')}: ${contact.orgNumber}`);
    if (contact.name) lines.push(contact.name);
    if (contact.email) lines.push(contact.email);
    if (contact.phone) lines.push(contact.phone);
    lines.forEach((line) => {
      doc.text(line, margin, cursorY);
      cursorY += 5;
    });
    cursorY += 4;
  }

  // ---------- PRODUCTS TABLE ----------
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.setTextColor('#1c1917');
  doc.text(
    `${t('Produktspecifikation', 'Product specification')} (${items.length})`,
    margin,
    cursorY,
  );
  cursorY += 4;

  // Build rows
  const tableRows = items.map((item, idx) => {
    const name = isSv ? item.productName_sv : item.productName_en;
    const variantStr = item.variant
      ? Object.entries(item.variant)
          .filter(([k, v]) => v && k !== 'image_url')
          .map(([k, v]) => `${k}: ${v}`)
          .join('  ·  ')
      : '';
    const articleNo =
      item.variant?.Artikelnummer ||
      item.variant?.artikelnummer ||
      item.variant?.['Artikelnummer'] ||
      '-';
    return [
      String(idx + 1),
      articleNo,
      [name, variantStr].filter(Boolean).join('\n'),
      String(item.quantity || 1),
    ];
  });

  autoTable(doc, {
    startY: cursorY,
    head: [
      [
        '#',
        t('Artikelnr', 'Article no'),
        t('Produkt / Variant', 'Product / Variant'),
        t('Antal', 'Qty'),
      ],
    ],
    body: tableRows,
    theme: 'grid',
    headStyles: {
      fillColor: [50, 26, 39], // #321a27
      textColor: [255, 255, 255],
      fontStyle: 'bold',
      fontSize: 9,
    },
    bodyStyles: {
      fontSize: 9,
      cellPadding: 3,
      textColor: [40, 40, 40],
    },
    alternateRowStyles: {
      fillColor: [250, 250, 248],
    },
    columnStyles: {
      0: { cellWidth: 10, halign: 'center' },
      1: { cellWidth: 30, fontStyle: 'bold' },
      2: { cellWidth: 'auto' },
      3: { cellWidth: 18, halign: 'center' },
    },
    margin: { left: margin, right: margin },
  });

  let afterTableY = doc.lastAutoTable.finalY + 8;

  // ---------- MESSAGE / NOTES ----------
  if (contact.message) {
    if (afterTableY > 240) {
      doc.addPage();
      afterTableY = 20;
    }
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.setTextColor('#1c1917');
    doc.text(t('Meddelande', 'Message'), margin, afterTableY);
    afterTableY += 6;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.setTextColor('#44403c');
    const splitMsg = doc.splitTextToSize(contact.message, pageWidth - 2 * margin);
    doc.text(splitMsg, margin, afterTableY);
    afterTableY += splitMsg.length * 5 + 4;
  }

  // ---------- FOOTER ----------
  const pageCount = doc.internal.pages.length - 1;
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    const ph = doc.internal.pageSize.getHeight();
    doc.setDrawColor('#e7e5e4');
    doc.setLineWidth(0.2);
    doc.line(margin, ph - 16, pageWidth - margin, ph - 16);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor('#78716c');
    doc.text(
      t(
        'Denna offertspec är genererad från flowingo.se · Priser och lagerstatus tillkommer vid offerering.',
        'This quote spec is generated from flowingo.se · Prices and stock status will be confirmed in the official quote.',
      ),
      margin,
      ph - 11,
    );
    doc.text(`${t('Sida', 'Page')} ${i} / ${pageCount}`, pageWidth - margin, ph - 11, {
      align: 'right',
    });
    doc.text('flowingo.se', margin, ph - 6);
    doc.text(refNumber, pageWidth - margin, ph - 6, { align: 'right' });
  }

  return { doc, refNumber };
};

// Helper: fetch a remote image and convert it to a data URL (so jsPDF can embed it)
const fetchAsDataUrl = async (url) => {
  try {
    const res = await fetch(url, { mode: 'cors' });
    if (!res.ok) return null;
    const blob = await res.blob();
    return await new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = () => resolve(null);
      reader.readAsDataURL(blob);
    });
  } catch (err) {
    return null;
  }
};
